import { IFBCallData } from "@sdk/fb-call-logs/fb-call-logs-model";
import { GlobalConfig } from "config";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  startCallRecording,
  type CallRecordingHandle,
} from "../utils/call-recording";

export interface StartRecordingConfig {
  organizationId: string;
  conversationId: string;
  callId: string;
  userId: string;
  engineUrl?: string;
}

interface UseWebRTCOutgoingFBCallReturn {
  peerConnection: RTCPeerConnection | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isSpeakerOn: boolean;
  initiateCall: () => Promise<string>;
  /** Apply Meta's SDP answer from `/facebook/initiate-call` (or webhook). */
  applyRemoteAnswer: (sdpAnswer: string) => Promise<void>;
  rejectCall: () => Promise<void>;
  terminateCall: () => Promise<void>;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  setAudioInputDevice: (deviceId: string) => Promise<void>;
  setAudioOutputDevice: (deviceId: string) => Promise<void>;
  audioInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  selectedAudioInput: string | null;
  selectedAudioOutput: string | null;
  callDuration: number;
  isProcessing: boolean;
  isRecording: boolean;
}

export const useWebRTCOutgoingFBCall = (
  callData?: IFBCallData,
  recordingConfig?: StartRecordingConfig | null,
): UseWebRTCOutgoingFBCallReturn => {
  const [peerConnection, setPeerConnection] =
    useState<RTCPeerConnection | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>(
    [],
  );
  const [audioOutputDevices, setAudioOutputDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState<string | null>(
    null,
  );
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string | null>(
    null,
  );

  const durationIntervalRef = useRef<number | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const audioElementsRef = useRef<HTMLAudioElement[]>([]);
  const recordingHandleRef = useRef<CallRecordingHandle | null>(null);
  const recordingConfigRef = useRef<StartRecordingConfig | null | undefined>(
    recordingConfig,
  );
  const [isRecording, setIsRecording] = useState(false);

  recordingConfigRef.current = recordingConfig;

  // Helper function to safely clean up audio elements
  const cleanupAudioElements = useCallback(() => {
    audioElementsRef.current.forEach((audio) => {
      try {
        if (audio && audio.srcObject) {
          audio.srcObject = null;
          audio.pause();
        }
      } catch (error) {
        console.warn("Error cleaning up audio element:", error);
      }
    });
    audioElementsRef.current = [];
  }, []);

  // Initialize WebRTC configuration
  const rtcConfiguration: RTCConfiguration = useMemo(
    () => ({
      iceServers: [
        { urls: "stun:stun.relay.metered.ca:80" },
        // { urls: "stun:stun.l.google.com:19302" },
        // { urls: "stun:stun1.l.google.com:19302" },
      ],
      // iceCandidatePoolSize: 10,
    }),
    [],
  );

  // Get available audio devices
  const getAudioDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((device) => device.kind === "audioinput");
      const outputs = devices.filter((device) => device.kind === "audiooutput");

      setAudioInputDevices(inputs);
      setAudioOutputDevices(outputs);

      // Set default devices
      if (inputs.length > 0 && !selectedAudioInput && inputs[0].deviceId) {
        setSelectedAudioInput(inputs[0].deviceId);
      }
      if (outputs.length > 0 && !selectedAudioOutput && outputs[0].deviceId) {
        setSelectedAudioOutput(outputs[0].deviceId);
      }
    } catch (error) {
      console.error("Error getting audio devices:", error);
    }
  }, [selectedAudioInput, selectedAudioOutput]);

  // Get user media with specific audio device
  const getUserMedia = useCallback(async (audioDeviceId?: string) => {
    try {
      const constraints: MediaStreamConstraints = {
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
        // video: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (error) {
      console.error("Error getting user media:", error);
      throw error;
    }
  }, []);

  // Recording (internal – auto-started when both streams ready and recordingConfig provided)
  const startRecordingInternal = useCallback(
    async (
      config: StartRecordingConfig,
    ): Promise<CallRecordingHandle | null> => {
      const local = localStreamRef.current;
      const remote = remoteStreamRef.current;
      if (!local || !remote) return null;
      if (recordingHandleRef.current) return recordingHandleRef.current;
      try {
        const handle = await startCallRecording(local, remote, {
          ...config,
          engineUrl: config.engineUrl ?? GlobalConfig.MEDIA_RECORDER_ENGINE_URL,
        });
        recordingHandleRef.current = handle;
        setIsRecording(true);
        return handle;
      } catch (err) {
        console.error("Failed to start recording:", err);
        return null;
      }
    },
    [],
  );

  const stopRecordingInternal = useCallback(async (): Promise<void> => {
    if (recordingHandleRef.current) {
      await recordingHandleRef.current.stop();
      recordingHandleRef.current = null;
    }
    setIsRecording(false);
  }, []);

  // Initiate call and create SDP offer
  // For outgoing calls, we need to:
  // 1. Create peer connection
  // 2. Get user media and add tracks
  // 3. Create an SDP OFFER (business initiates, so we create the offer)
  // 4. Set it as local description
  // 5. Return the offer SDP to send to Messenger
  // Note: Messenger answer will come later via webhook and should be set as remote description
  const initiateCall = useCallback(async (): Promise<string> => {
    setIsProcessing(true);
    try {
      // Clear any previous recording handle so the second (or later) call can start recording.
      await stopRecordingInternal();
      // Only clear remote ref if we had one (reuse from a previous call). Avoids breaking the first call
      // where the effect or ontrack need to see the new remote; clearing unconditionally left remote null
      // when config appeared before ontrack fired.
      if (remoteStreamRef.current != null) {
        remoteStreamRef.current = null;
      }
      console.log("Starting initiate outgoing call with Messenger...");

      // Create peer connection but don't assign to pcRef yet - unmount cleanup would close it
      // during await getUserMedia (e.g. React Strict Mode) and then addTrack would throw.
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.relay.metered.ca:80" }],
      });

      // Get user's microphone (await point: component may unmount here; cleanup must not close our pc)
      const localStream = await getUserMedia(selectedAudioInput || undefined);
      console.log(
        "Local stream obtained:",
        localStream.getTracks().length,
        "tracks",
      );

      if (pc.signalingState === "closed") {
        localStream.getTracks().forEach((t) => t.stop());
        throw new Error("PeerConnection was closed before adding tracks");
      }

      // Set local stream ref after obtaining it
      localStreamRef.current = localStream;

      // Add local audio track to peer connection
      localStream.getTracks().forEach((track) => {
        console.log("Adding track to peer connection:", track.kind, track.id);
        pc.addTrack(track, localStream);
      });

      // Handle incoming audio from Messenger (will be received after they accept)
      pc.ontrack = (event) => {
        console.log("Received audio track from Messenger");
        remoteStreamRef.current = event.streams[0];
        // Auto-start recording when both streams are ready (if config provided)
        const config = recordingConfigRef.current;
        if (
          config &&
          localStreamRef.current &&
          event.streams[0] &&
          !recordingHandleRef.current
        ) {
          startRecordingInternal(config).catch((err) =>
            console.error("Auto-start recording failed:", err),
          );
        }
      };

      // For outgoing calls, we CREATE an offer (not answer)
      console.log("Creating SDP offer for outgoing call...");
      const offer = await pc.createOffer();
      console.log("SDP offer created");

      // Set the offer as local description
      await pc.setLocalDescription(offer);
      console.log("Local description set with offer");

      // Only now expose to ref/state so unmount cleanup won't close this pc during setup
      pcRef.current = pc;
      setPeerConnection(pc);

      // Start call duration timer (will start counting once call is connected)
      durationIntervalRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);

      console.log("SDP offer ready to send to Messenger:", offer.sdp);
      return offer.sdp || "";
    } catch (error) {
      console.error("Error creating outgoing call offer:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedAudioInput,
    getUserMedia,
    startRecordingInternal,
    stopRecordingInternal,
  ]);

  const applyRemoteAnswer = useCallback(async (sdpAnswer: string) => {
    const pc = pcRef.current;
    if (!pc || !sdpAnswer) {
      throw new Error("No peer connection or SDP answer to apply");
    }
    if (pc.remoteDescription) {
      return;
    }
    await pc.setRemoteDescription(
      new RTCSessionDescription({
        type: "answer",
        sdp: sdpAnswer,
      }),
    );
    console.log("Remote description set with Messenger answer");
  }, []);

  // Reject call
  const rejectCall = useCallback(async (): Promise<void> => {
    setIsProcessing(true);
    try {
      await stopRecordingInternal();
      if (pcRef.current) {
        pcRef.current.close();
        setPeerConnection(null);
        pcRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }

      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      // Remove audio elements from document - use more specific selector and add error handling
      // try {
      //   // Only remove audio elements that are part of this call
      //   const audioElements = document.querySelectorAll("audio");
      //   audioElements.forEach((audio) => {
      //     if (audio.srcObject && audio.parentNode) {
      //       try {
      //         audio.parentNode.removeChild(audio);
      //       } catch (removeError) {
      //         // If removeChild fails, try to clear the srcObject
      //         audio.srcObject = null;
      //         audio.remove();
      //       }
      //     }
      //   });
      // } catch (error) {
      //   console.warn("Error removing audio elements:", error);
      // }

      setCallDuration(0);
      remoteStreamRef.current = null;
    } catch (error) {
      console.error("Error rejecting call:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [stopRecordingInternal]);

  // Terminate call
  const terminateCall = useCallback(async (): Promise<void> => {
    return rejectCall();
  }, [rejectCall]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  // Toggle speaker
  const toggleSpeaker = useCallback(() => {
    const newSpeakerState = !isSpeakerOn;
    setIsSpeakerOn(newSpeakerState);

    // Apply speaker state to audio elements
    try {
      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audio) => {
        if (audio.srcObject) {
          if (newSpeakerState) {
            // Speaker on: unmute and set full volume
            audio.muted = false;
            audio.volume = 1.0;
            // Try to enable speaker if supported
            if ("sinkId" in audio && selectedAudioOutput) {
              (audio as any).setSinkId(selectedAudioOutput);
            }
          } else {
            // Speaker off: mute the audio
            audio.muted = true;
          }
        }
      });
    } catch (error) {
      console.warn("Error toggling speaker:", error);
    }
  }, [isSpeakerOn, selectedAudioOutput]);

  // Set audio input device
  const setAudioInputDevice = useCallback(
    async (deviceId: string) => {
      try {
        setSelectedAudioInput(deviceId);

        if (localStreamRef.current) {
          // Store current mute state
          const currentTrack = localStreamRef.current.getAudioTracks()[0];
          const wasMuted = currentTrack ? !currentTrack.enabled : false;

          // Stop current audio track
          if (currentTrack) {
            currentTrack.stop();
          }

          // Get new stream with selected device
          const newStream = await getUserMedia(deviceId);

          // Replace track in peer connection
          if (pcRef.current) {
            const senders = pcRef.current.getSenders();
            const audioSender = senders.find(
              (sender) => sender.track?.kind === "audio",
            );
            if (audioSender) {
              const newTrack = newStream.getAudioTracks()[0];
              await audioSender.replaceTrack(newTrack);
            }
          }

          // Preserve mute state
          const newTrack = newStream.getAudioTracks()[0];
          if (newTrack && wasMuted) {
            newTrack.enabled = false;
          }

          localStreamRef.current = newStream;
        }
      } catch (error) {
        console.error("Error setting audio input device:", error);
      }
    },
    [getUserMedia],
  );

  // Set audio output device
  const setAudioOutputDevice = useCallback(
    async (deviceId: string) => {
      try {
        setSelectedAudioOutput(deviceId);

        // Note: Audio output device selection is handled by the browser
        // and the audio element's sinkId property (if supported)
        const audioElements = document.querySelectorAll("audio");
        audioElements.forEach((audio) => {
          if ("sinkId" in audio) {
            (audio as any).setSinkId(deviceId);
          }
        });

        // Update speaker state if speaker is on
        if (isSpeakerOn) {
          const audioElements = document.querySelectorAll("audio");
          audioElements.forEach((audio) => {
            if (audio.srcObject) {
              audio.muted = false;
              audio.volume = 1.0;
            }
          });
        }
      } catch (error) {
        console.error("Error setting audio output device:", error);
      }
    },
    [isSpeakerOn],
  );

  // Handle Messenger answer SDP from webhook callData (fallback if not applied from initiate response)
  const messengerAnswerSdp =
    (typeof callData?.session?.sdp_response === "object"
      ? callData.session.sdp_response?.sdp
      : typeof callData?.session?.sdp_response === "string"
      ? callData.session.sdp_response
      : undefined) ||
    callData?.session?.sdp ||
    callData?.connection?.webrtc?.sdp;

  useEffect(() => {
    if (!pcRef.current || !messengerAnswerSdp) {
      return;
    }

    if (pcRef.current.localDescription && !pcRef.current.remoteDescription) {
      console.log(
        "Received Messenger answer SDP via callData, setting as remote description",
      );
      applyRemoteAnswer(messengerAnswerSdp).catch((error) => {
        console.error("Error setting remote description with answer:", error);
      });
    }
  }, [messengerAnswerSdp, applyRemoteAnswer]);

  // Start recording when config becomes available after streams are already ready.
  // Handles the case where ontrack fired before callLog (and thus recordingConfig) existed (e.g. second call from floating panel).
  useEffect(() => {
    const config = recordingConfigRef.current;
    if (!config) return;
    const local = localStreamRef.current;
    const remote = remoteStreamRef.current;
    if (!local || !remote || recordingHandleRef.current) return;
    startRecordingInternal(config).catch((err) =>
      console.error("Auto-start recording (effect) failed:", err),
    );
  }, [recordingConfig, startRecordingInternal]);

  // Initialize audio devices on mount
  useEffect(() => {
    getAudioDevices();

    // Listen for device changes
    navigator.mediaDevices.addEventListener("devicechange", getAudioDevices);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        getAudioDevices,
      );
    };
  }, [getAudioDevices]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecordingInternal().catch((err) =>
        console.error("Error stopping recording:", err),
      );
      if (pcRef.current) {
        pcRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      // Remove audio elements from document - use more specific selector and add error handling
      try {
        // Only remove audio elements that are part of this call
        const audioElements = document.querySelectorAll("audio");
        audioElements.forEach((audio) => {
          if (audio.srcObject && audio.parentNode) {
            try {
              audio.parentNode.removeChild(audio);
            } catch (removeError) {
              // If removeChild fails, try to clear the srcObject
              audio.srcObject = null;
              audio.remove();
            }
          }
        });
      } catch (error) {
        console.warn("Error removing audio elements:", error);
      }
    };
  }, [stopRecordingInternal]);

  return {
    peerConnection,
    localStream: localStreamRef.current,
    remoteStream: remoteStreamRef.current,
    isMuted,
    isSpeakerOn,
    initiateCall,
    applyRemoteAnswer,
    rejectCall,
    terminateCall,
    toggleMute,
    toggleSpeaker,
    setAudioInputDevice,
    setAudioOutputDevice,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    callDuration,
    isProcessing,
    isRecording,
  };
};
